import React, { useMemo, useState } from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { BookOpen, Info, Sparkles } from 'lucide-react';

export const RULES_REGISTRY = {
  open_pipeline: {
    title: 'Open Pipeline',
    description: 'Open pipeline includes only active opportunities still in play.',
    bullets: ['Includes: Lead, Qualified, Proposal, Negotiation', 'Excludes: Won, Lost, Hold, archived / inactive deals'],
  },
  won_lost_hold: {
    title: 'Won / Lost / Hold',
    description: 'Opportunity classification used across Opportunities, dashboards, and AI analytics.',
    bullets: ['Won = closed as won', 'Lost = closed as lost / junk / archived with lost reason', 'Hold = paused deal, not counted as open pipeline or lost'],
  },
  invoice_progress: {
    title: 'Invoice Progress',
    description: 'Sales Order invoicing status from Odoo custom field `custom_invoicing_status`.',
    bullets: ['Not Invoiced = no invoice made', 'Partially Invoiced = some invoicing done', 'Fully Invoiced = all invoicing done'],
  },
  collection_status: {
    title: 'Collection Status',
    description: 'Invoice collection metrics used on Invoices and AI analytics.',
    bullets: ['Collected = paid invoices', 'Overdue = unpaid / partial invoices past due date', 'Pending = unpaid invoices not yet overdue'],
  },
  totals_scope: {
    title: 'Totals Scope',
    description: 'How totals are interpreted across pages.',
    bullets: ['KPI cards reflect the active contextual filters', 'Search typically refines only the visible list/table', 'Some summary rows reflect the current page slice after search'],
  },
};

export function BusinessRulesBar({
  title = 'Scope & Business Rules',
  description = 'This view follows standardized business rules and filter scope.',
  scopeItems = [],
  ruleKeys = ['totals_scope'],
  dataTestId = 'business-rules-bar',
}) {
  const [open, setOpen] = useState(false);
  const scopeBadges = useMemo(() => scopeItems.filter(Boolean), [scopeItems]);
  const rules = useMemo(() => ruleKeys.map((key) => RULES_REGISTRY[key]).filter(Boolean), [ruleKeys]);

  return (
    <>
      <div className="rounded-2xl border bg-white/80 backdrop-blur px-3 py-2 flex flex-wrap items-center justify-between gap-3" data-testid={dataTestId}>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-slate-800">
            <Sparkles className="h-4 w-4 text-[#800000]" />
            <p className="text-xs font-semibold">{title}</p>
          </div>
          <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">{description}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {scopeBadges.length > 0 ? scopeBadges.slice(0, 3).map((item) => (
            <Badge key={item} variant="outline" className="text-[10px] bg-slate-50 text-slate-700 border-slate-200">
              {item}
            </Badge>
          )) : (
            <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-500 border-slate-200">All active data</Badge>
          )}
          <Button variant="outline" size="sm" className="shrink-0 h-7 text-[10px] px-2.5" onClick={() => setOpen(true)} data-testid={`${dataTestId}-open-button`}>
          <BookOpen className="h-4 w-4 mr-1" /> Business Rules
          </Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl" data-testid={`${dataTestId}-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Info className="h-4 w-4 text-[#800000]" /> Business Rules Registry</DialogTitle>
            <DialogDescription>
              These are the shared definitions currently used by this module so numbers stay consistent across dashboards, analytics, and detail views.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {rules.map((rule) => (
              <div key={rule.title} className="rounded-xl border bg-slate-50 p-4">
                <h4 className="text-sm font-semibold text-slate-800">{rule.title}</h4>
                <p className="text-xs text-slate-600 mt-1">{rule.description}</p>
                <ul className="mt-3 space-y-1.5">
                  {rule.bullets.map((bullet) => (
                    <li key={bullet} className="text-xs text-slate-700 flex gap-2">
                      <span className="text-[#800000]">•</span>
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
